import React, { useState, useRef, useEffect } from "react";
import { Box, Tabs, TabList, Tab, Accordion, Text, AspectRatio } from "@chakra-ui/react";
import { isSafari } from "react-device-detect";
import { Section } from "components/section";
import { LottieWithSafariVideo, Lottie } from "ui/lottie";
import { FillImage, DecorativeBackgroundImage } from "components/images";
import { GradientAccordionItem } from "ui/accordion/gradient-accordian-item";
import useCase1Animation from "components/animations/agents/use-case-1.json";
import useCase2Animation from "components/animations/agents/use-case-2.json";
import useCase3Animation from "components/animations/agents/use-case-3.json";
import useCase4Animation from "components/animations/agents/use-case-4.json";
import useCase5Animation from "components/animations/agents/use-case-5.json";
import useCase1Image from "../../../../public/assets/platform/agents/use-case-1.png";
import useCase2Image from "../../../../public/assets/platform/agents/use-case-2.png";
import useCase3Image from "../../../../public/assets/platform/agents/use-case-3.png";
import useCase4Image from "../../../../public/assets/platform/agents/use-case-4.png";
import useCase5Image from "../../../../public/assets/platform/agents/use-case-5.png";
import blueGradient from "../../../../public/assets/platform/agents/blue-gradient.png";

const ANIMATION_RATIO = 2640 / 1480;
const TAB_BG_GRADIENT = "linear-gradient(180deg, #CCF0F9 46.49%, #80C4EA 174.96%, #3498DA 303.42%, #164FAA 531.58%)";
const ACCORDION_BG_GRADIENT = "linear-gradient(180deg, #CCF0F9 23.71%, #8BCDF0 54.96%, #2D71DC 103.45%)";

// Shared data source
const useCases = [
  {
    id: "marketing-campaign",
    title: "Plan marketing campaigns",
    description: "Ideate and write winning campaign briefs",
    mobileImage: useCase1Image,
    mobileImageRatio: 343 / 450,
    animationData: useCase1Animation,
    animationMp4: "https://cdn.sanity.io/files/pwmfmi47/production/755e7142d994d7306158540f8e5b59bbd5e80791.mp4",
    duration: 12_000,
  },
  {
    id: "customer-data",
    title: "Ask any customer data question",
    description: "Stop waiting on data requests, get answers immediately",
    mobileImage: useCase2Image,
    mobileImageRatio: 343 / 340,
    animationData: useCase2Animation,
    animationMp4: "https://cdn.sanity.io/files/pwmfmi47/production/d0e2330fad9bc0fadc250320ab56d3be75253294.mp4",
    duration: 7000,
  },
  {
    id: "creative-analysis",
    title: "Analyze creative & copy",
    description: "Discover what is and isn’t working and how to improve",
    mobileImage: useCase3Image,
    mobileImageRatio: 343 / 380,
    animationData: useCase3Animation,
    animationMp4: "https://cdn.sanity.io/files/pwmfmi47/production/7b62e5749146919286a1981388a7721e729aa2bd.mp4",
    duration: 7000,
  },
  {
    id: "audience-discovery",
    title: "Discover new audiences",
    description: "Find the right audience for every campaign",
    mobileImage: useCase4Image,
    mobileImageRatio: 343 / 420,
    animationData: useCase4Animation,
    animationMp4: "https://cdn.sanity.io/files/pwmfmi47/production/1d2279fc9f893fdbbf8bc7db7ffd02de4ab292a5.mp4",
    duration: 7000,
  },
  {
    id: "automated-reporting",
    title: "Automate reporting",
    description: "Save hours every Monday, let AI take a first pass on “why”",
    mobileImage: useCase5Image,
    mobileImageRatio: 343 / 470,
    animationData: useCase5Animation,
    animationMp4: "https://cdn.sanity.io/files/pwmfmi47/production/24316001e7a6d622838f131f2f05fd6c08379838.mp4",
    duration: 13_000,
  },
];

export function AgentsUseCases() {
  return (
    <Section id="use-cases" my={0}>
      <Box display={{ base: "block", lg: "none" }} pt="tiny">
        <AccordionLayout />
      </Box>
      <Box display={{ base: "none", lg: "block" }}>
        <TabsLayout />
      </Box>
    </Section>
  );
}

/* ---------- Tabs Layout ---------- */

function TabsLayout() {
  const [activeTab, setActiveTab] = useState(0);

  // Auto progression with looping
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTab((previous) => (previous === useCases.length - 1 ? 0 : previous + 1));
    }, useCases[activeTab].duration);

    return () => {
      clearInterval(interval);
    };
  }, [activeTab]);

  return (
    <Box maxW="1000px" mx="auto">
      <Tabs index={activeTab} variant="unstyled" onChange={setActiveTab}>
        <TabList overflowX="auto" gap="smaller" flexWrap={{ base: "wrap", md: "nowrap" }}>
          {useCases.map((uc, i) => (
            <UseCaseTab key={uc.id} title={uc.title} description={uc.description} isSelected={activeTab === i} />
          ))}
        </TabList>

        <Box position="relative" h="auto" mt="smaller" borderRadius="smaller" overflow="hidden">
          <Box pos="absolute" inset={0} zIndex={0}>
            <DecorativeBackgroundImage src={blueGradient} style={{ objectFit: "cover" }} />
          </Box>

          {/* Keep all panels mounted and use CSS to show/hide to prevent flashing */}
          <Box position="relative" zIndex={1} h="auto">
            {useCases.map((uc, index) => (
              <Box
                key={uc.id}
                position={index === activeTab ? "relative" : "absolute"}
                top={0}
                left={0}
                right={0}
                opacity={index === activeTab ? 1 : 0}
                visibility={index === activeTab ? "visible" : "hidden"}
                pointerEvents={index === activeTab ? "auto" : "none"}
                transition="opacity 0.2s ease-in-out"
                h="100%"
                p={0}
              >
                {uc.id === "marketing-campaign" ? (
                  <LottieWithSafariVideoInline
                    animationData={uc.animationData}
                    videoSrc={uc.animationMp4}
                    useCaseId={uc.id}
                    isActive={index === activeTab}
                  />
                ) : (
                  <LottieWithSafariVideo
                    viewportAmount={index === activeTab ? 0.1 : 0}
                    ratio={ANIMATION_RATIO}
                    animationData={uc.animationData}
                    videoSrc={uc.animationMp4}
                    videoBorderRadius={uc.id === "customer-data" ? 0 : "bigger"}
                    placeholderBgColor="transparent"
                  />
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Tabs>
    </Box>
  );
}

/* ---------- Lottie with Safari Video (Inline) ---------- */

function LottieWithSafariVideoInline({
  animationData,
  videoSrc,
  useCaseId,
  isActive = true,
}: {
  animationData: unknown;
  videoSrc: string;
  useCaseId: string;
  isActive?: boolean;
}) {
  const [isClient, setIsClient] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoBorderRadius = useCaseId === "customer-data" ? 0 : "bigger";
  const shouldScaleLottie = useCaseId === "marketing-campaign";

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Control video playback based on active state
  useEffect(() => {
    if (isClient && isSafari && videoRef.current) {
      if (isActive) {
        videoRef.current.play().catch(() => {
          // Ignore play errors
        });
      } else {
        videoRef.current.pause();
      }
    }
  }, [isActive, isClient]);

  if (!isClient) {
    return (
      <AspectRatio ratio={ANIMATION_RATIO} w="100%" borderRadius={videoBorderRadius} overflow="hidden">
        <Box bg="transparent" boxSize="100%" />
      </AspectRatio>
    );
  }

  if (isSafari) {
    return (
      <AspectRatio ratio={ANIMATION_RATIO} w="100%" borderRadius={videoBorderRadius} overflow="hidden">
        <video ref={videoRef} loop muted playsInline autoPlay={isActive} width="100%" height="100%" preload="metadata">
          <source src={videoSrc} type="video/mp4" />
        </video>
      </AspectRatio>
    );
  }

  const lottie = (
    <Lottie
      viewportAmount={isActive ? 0.1 : 0}
      ratio={ANIMATION_RATIO}
      animationData={animationData}
      containerProps={
        shouldScaleLottie ? { w: "100%", maxW: "100%" } : { borderRadius: videoBorderRadius, overflow: "hidden" }
      }
    />
  );

  if (shouldScaleLottie) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        h="100%"
        transform="scale(0.83)"
        transformOrigin="center"
      >
        {lottie}
      </Box>
    );
  }

  return lottie;
}

/* ---------- Accordion Layout ---------- */

function AccordionLayout() {
  return (
    <Accordion allowToggle defaultIndex={0} display="flex" flexDir="column" gap="smaller">
      {useCases.map((uc) => (
        <GradientAccordionItem
          key={uc.id}
          title={uc.title}
          description={uc.description}
          media={<FillImage src={uc.mobileImage} alt={uc.title} />}
          mediaRatio={uc.mobileImageRatio}
          activeGradientColor={ACCORDION_BG_GRADIENT}
          inactiveBorderColor="new.blue.300"
          buttonColor="new.blue.50"
        />
      ))}
    </Accordion>
  );
}

/* ---------- Shared Tab Component ---------- */

function UseCaseTab({ title, description, isSelected }: { title: string; description: string; isSelected: boolean }) {
  return (
    <Tab
      position="relative"
      display="flex"
      flexDir="column"
      justifyContent="flex-start"
      alignItems="flex-start"
      p="smaller"
      gap="tiny"
      borderRadius="tiny"
      border="1px solid"
      borderColor={isSelected ? "new.blue.150" : "new.blue.500"}
      transition="border-color 0.1s ease-out"
      w="100%"
      className="group"
      overflow="hidden"
      _before={{
        content: '""',
        position: "absolute",
        inset: 0,
        bg: TAB_BG_GRADIENT,
        opacity: isSelected ? 1 : 0,
        transition: "opacity 0.2s ease-out",
        pointerEvents: "none",
        zIndex: 0,
      }}
      _hover={{
        _before: {
          opacity: isSelected ? 1 : 0.25,
        },
      }}
    >
      <Text
        position="relative"
        zIndex={1}
        fontSize="16px"
        fontWeight="bold"
        textAlign="left"
        lineHeight={1.1}
        color={isSelected ? "" : "brand.text.secondary"}
        transition="color 0.1s ease-out"
      >
        {title}
      </Text>
      <Text
        position="relative"
        zIndex={1}
        fontSize="12px"
        textAlign="left"
        color={isSelected ? "" : "brand.text.secondary"}
        transition="color 0.2s ease-out"
      >
        {description}
      </Text>
    </Tab>
  );
}
